import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';

export const ContactSection = () => {
  const { toast } = useToast();
  const sectionRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectName: 'One by MSN',
    website_name: 'onebymsn-neopolis.com'

  });

  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    //form validation can be added here
    if (!formData.name || !formData.email || !formData.phone) {
      toast({
        title: "Incomplete Form",
        description: "Please fill in all required fields before submitting.",
        variant: "destructive",
      });
      return;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }
    if (!/^\d{10}$/.test(formData.phone)) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid 10-digit phone number.",
        variant: "destructive",
      });
      return;
    }
    if (formData.name.length < 2) {
      toast({
        title: "Invalid Name",
        description: "Name should be at least 2 characters long.",
        variant: "destructive",
      });
      return;
    }

    // Simulate API call or backend integration
    const response = await fetch(
      "https://ycb1sb6t8e.execute-api.us-west-2.amazonaws.com/default/LeadStorw",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      },
    );
    if (!response.ok) {
      // Handle error
      toast({
        title: "Submission Failed",
        description:
          "There was an error submitting your enquiry. Please try again later.",
        variant: "destructive",
      });
      return;
    } else {
      toast({
        title: "Enquiry Submitted!",
        description: "Our team will contact you within 24 hours.",
      });

      navigate('/thank-you');

      setFormData({ name: "", email: "", phone: "" });
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-amber-50 via-white to-yellow-50 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-gradient-to-br from-amber-400/20 to-yellow-500/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-br from-yellow-400/20 to-amber-500/20 rounded-full blur-3xl animate-float-delayed"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-2xl mx-auto">
          {/* Title */}
          <h2 className="text-5xl font-bold mb-16 text-center">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 via-yellow-600 to-amber-600 animate-shimmer">
              Contact Us
            </span>
          </h2>

          {/* Contact Form */}
          <div 
            ref={sectionRef}
            className="luxury-card bg-white rounded-2xl p-10 shadow-2xl border-2 border-amber-200/50 hover:border-amber-400 opacity-0 translate-y-10 transition-all duration-1000"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="transform hover:scale-105 transition-transform duration-300">
                <Input
                  placeholder="Name:"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-gradient-to-r from-amber-50/50 to-yellow-50/50 border-2 border-amber-200 focus:border-amber-500 text-gray-900 placeholder:text-gray-500 py-7 text-lg rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                />
              </div>

              <div className="transform hover:scale-105 transition-transform duration-300">
                <Input
                  type="email"
                  placeholder="Email:"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-gradient-to-r from-amber-50/50 to-yellow-50/50 border-2 border-amber-200 focus:border-amber-500 text-gray-900 placeholder:text-gray-500 py-7 text-lg rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                />
              </div>

              <div className="transform hover:scale-105 transition-transform duration-300">
                <Input
                  type="tel"
                  placeholder="Phone:"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="bg-gradient-to-r from-amber-50/50 to-yellow-50/50 border-2 border-amber-200 focus:border-amber-500 text-gray-900 placeholder:text-gray-500 py-7 text-lg rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 hover:from-amber-600 hover:via-yellow-600 hover:to-amber-600 text-black font-bold py-7 text-lg shadow-2xl shadow-amber-500/30 hover:shadow-amber-500/60 transform hover:scale-105 transition-all duration-300"
              >
                Submit
              </Button>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </section>
  );
};
